import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getUserToken } from '@/redux/user';
import {
    useLazyGetPromotionalBannersQuery,
    useTogglePromotionalBannerMutation,
    useToggleRestaurantBrandBannerMutation,
} from '@/services/innovorder/promotionalBanner/promotionalBanner.endpoints';
import type { PromotionalBanner } from '@/services/innovorder/promotionalBanner/promotionalBanner.type';

export const useDeactivateBannersOnMenuChange = () => {
    const token = useSelector(getUserToken);
    const [getBanners] = useLazyGetPromotionalBannersQuery();
    const [toggleBanner] = useTogglePromotionalBannerMutation();
    const [toggleBrandBannerForRestaurant] = useToggleRestaurantBrandBannerMutation();
    const intl = useIntl();

    const getActiveBanners = useCallback(
        async (restaurantId: number): Promise<PromotionalBanner[]> => {
            if (!token) return [];
            const { data } = await getBanners({ token, restaurantId });
            return data?.filter((b) => b.isActive) ?? [];
        },
        [getBanners, token],
    );

    const deactivateAndNotify = useCallback(
        async (banners: PromotionalBanner[], restaurantId: number): Promise<void> => {
            if (!token || banners.length === 0) return;

            await Promise.all(
                banners.map((banner) =>
                    banner.brandId !== null
                        ? toggleBrandBannerForRestaurant({
                              token,
                              promotionalBannerId: banner.promotionalBannerId,
                              restaurantId,
                              isActive: false,
                          })
                        : toggleBanner({
                              token,
                              promotionalBannerId: banner.promotionalBannerId,
                              isActive: false,
                          }),
                ),
            );

            toast.info(intl.formatMessage({ id: 'menu.modal.bannersDeactivated' }, { count: banners.length }));
        },
        [toggleBanner, toggleBrandBannerForRestaurant, token, intl],
    );

    return { getActiveBanners, deactivateAndNotify };
};
